"use client";

import Image from "next/image";
import { Check, CheckCircle2, Copy, Loader, Sparkles } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocale } from "next-intl";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAppContext } from "@/contexts/app";
import { trackEvent } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import type { Pricing as PricingType, PricingItem } from "@/types/blocks/pricing";

const ZERO_DECIMAL_CURRENCIES = new Set([
  "bif",
  "clp",
  "djf",
  "gnf",
  "jpy",
  "kmf",
  "krw",
  "mga",
  "pyg",
  "rwf",
  "ugx",
  "vnd",
  "vuv",
  "xaf",
  "xof",
  "xpf",
]);

const minorToMajorCurrencyUnits = (amountMinor: number, currency: string) => {
  const normalizedCurrency = (currency || "").toLowerCase();
  if (ZERO_DECIMAL_CURRENCIES.has(normalizedCurrency)) {
    return Math.round(amountMinor);
  }

  return Math.round(amountMinor) / 100;
};

function ManualPaymentModal({
  isOpen,
  onClose,
  item,
}: {
  isOpen: boolean;
  onClose: () => void;
  item: PricingItem | null;
}) {
  const [step, setStep] = useState<"qr" | "confirm" | "success">("qr");
  const [orderNo, setOrderNo] = useState("");
  const [amountYuan, setAmountYuan] = useState(0);
  const [transactionId, setTransactionId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<"alipay" | "wechat">(
    "alipay"
  );
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const locale = useLocale();

  const alipayQrUrl = process.env.NEXT_PUBLIC_ALIPAY_QR_URL || "";
  const wechatQrUrl = process.env.NEXT_PUBLIC_WECHAT_QR_URL || "";

  useEffect(() => {
    if (isOpen && item) {
      setStep("qr");
      setTransactionId("");
      setPaymentMethod("alipay");
      setCopied(false);
      void createManualPaymentRequest();
    }
  }, [isOpen, item]);

  const createManualPaymentRequest = async () => {
    if (!item) return;

    setIsLoading(true);
    try {
      const response = await fetch("/api/manual-pay", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_id: item.product_id,
          locale: locale || "zh-cn",
        }),
      });

      const payload = await response.json();
      if (payload.code === 0 && payload.data) {
        setOrderNo(payload.data.order_no);
        setAmountYuan(payload.data.amount_yuan);
      } else {
        toast.error(payload.message || "创建支付请求失败");
        onClose();
      }
    } catch {
      toast.error("网络错误，请重试");
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const copyOrderNo = () => {
    navigator.clipboard.writeText(orderNo);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("订单号已复制");
  };

  const submitConfirmation = async () => {
    if (!transactionId.trim()) {
      toast.error("请输入交易号");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch("/api/manual-pay/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          order_no: orderNo,
          transaction_id: transactionId.trim(),
          payment_method: paymentMethod,
        }),
      });

      const payload = await response.json();
      if (payload.code === 0) {
        setStep("success");
        toast.success("支付信息已提交，请等待审核");
      } else {
        toast.error(payload.message || "提交失败");
      }
    } catch {
      toast.error("网络错误，请重试");
    } finally {
      setIsLoading(false);
    }
  };

  const currentQrUrl = paymentMethod === "alipay" ? alipayQrUrl : wechatQrUrl;

  if (!item) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {step === "qr" && "扫码支付"}
            {step === "confirm" && "确认支付"}
            {step === "success" && "提交成功"}
          </DialogTitle>
          <DialogDescription>
            {step === "qr" && (
              <>
                {item.product_name || item.title} - ¥
                {amountYuan || item.cn_amount! / 100}
              </>
            )}
            {step === "confirm" && "请输入您的支付交易号以确认付款"}
            {step === "success" && "您的支付信息已提交，审核通过后将自动到账"}
          </DialogDescription>
        </DialogHeader>

        {step === "qr" ? (
          <div className="space-y-4">
            <div className="rounded-lg bg-muted p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">订单号</span>
                <div className="flex items-center gap-2">
                  <code className="text-sm font-mono">{orderNo || "加载中..."}</code>
                  {orderNo ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={copyOrderNo}
                    >
                      {copied ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  ) : null}
                </div>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                请备注此订单号在转账说明中
              </p>
            </div>

            <Tabs
              value={paymentMethod}
              onValueChange={(value) =>
                setPaymentMethod(value as "alipay" | "wechat")
              }
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="alipay">支付宝</TabsTrigger>
                <TabsTrigger value="wechat">微信支付</TabsTrigger>
              </TabsList>

              <TabsContent value="alipay" className="mt-4">
                <div className="flex flex-col items-center space-y-3">
                  {alipayQrUrl ? (
                    <div className="relative h-48 w-48 overflow-hidden rounded-lg bg-white">
                      <Image
                        src={alipayQrUrl}
                        alt="支付宝收款码"
                        fill
                        className="object-contain p-2"
                      />
                    </div>
                  ) : (
                    <div className="flex h-48 w-48 items-center justify-center rounded-lg bg-muted text-sm text-muted-foreground">
                      未配置收款码
                    </div>
                  )}
                  <p className="text-center text-sm text-muted-foreground">
                    请使用支付宝扫码支付 ¥{amountYuan || item.cn_amount! / 100}
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="wechat" className="mt-4">
                <div className="flex flex-col items-center space-y-3">
                  {wechatQrUrl ? (
                    <div className="relative h-48 w-48 overflow-hidden rounded-lg bg-white">
                      <Image
                        src={wechatQrUrl}
                        alt="微信收款码"
                        fill
                        className="object-contain p-2"
                      />
                    </div>
                  ) : (
                    <div className="flex h-48 w-48 items-center justify-center rounded-lg bg-muted text-sm text-muted-foreground">
                      未配置收款码
                    </div>
                  )}
                  <p className="text-center text-sm text-muted-foreground">
                    请使用微信扫码支付 ¥{amountYuan || item.cn_amount! / 100}
                  </p>
                </div>
              </TabsContent>
            </Tabs>

            <div className="space-y-1 text-xs text-muted-foreground">
              <p>1. 请扫码支付对应金额</p>
              <p>2. 务必备注订单号：{orderNo || "..."}</p>
              <p>3. 支付完成后点击"我已支付"</p>
            </div>

            <Button
              className="w-full"
              onClick={() => setStep("confirm")}
              disabled={isLoading || !orderNo}
            >
              我已支付
            </Button>
          </div>
        ) : null}

        {step === "confirm" ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="payment-method">支付方式</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={paymentMethod === "alipay" ? "default" : "outline"}
                  onClick={() => setPaymentMethod("alipay")}
                  className="flex-1"
                >
                  支付宝
                </Button>
                <Button
                  type="button"
                  variant={paymentMethod === "wechat" ? "default" : "outline"}
                  onClick={() => setPaymentMethod("wechat")}
                  className="flex-1"
                >
                  微信支付
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="transaction-id">
                交易号 / 订单号
                <span className="ml-1 text-xs text-muted-foreground">
                  (可在账单详情中查看)
                </span>
              </Label>
              <Input
                id="transaction-id"
                placeholder="请输入交易号..."
                value={transactionId}
                onChange={(event) => setTransactionId(event.target.value)}
              />
            </div>

            <div className="rounded-lg bg-muted p-3 text-sm">
              <p className="font-medium">支付金额：¥{amountYuan}</p>
              <p className="text-muted-foreground">
                商品：{item.product_name || item.title}
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setStep("qr")}
                className="flex-1"
              >
                返回
              </Button>
              <Button
                onClick={submitConfirmation}
                disabled={isLoading || !transactionId.trim()}
                className="flex-1"
              >
                {isLoading ? <Loader className="mr-2 h-4 w-4 animate-spin" /> : null}
                提交确认
              </Button>
            </div>
          </div>
        ) : null}

        {step === "success" ? (
          <div className="space-y-4 py-4 text-center">
            <CheckCircle2 className="mx-auto h-16 w-16 text-green-500" />
            <div>
              <p className="font-medium">支付信息已提交</p>
              <p className="mt-1 text-sm text-muted-foreground">订单号：{orderNo}</p>
              <p className="text-sm text-muted-foreground">
                审核通过后积分将自动到账
              </p>
            </div>
            <Button onClick={onClose} className="w-full">
              完成
            </Button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default function Pricing({
  pricing,
  isStandalone = false,
}: {
  pricing: PricingType;
  isStandalone?: boolean;
}) {
  if (pricing.disabled) {
    return null;
  }

  const description = pricing.description
    ?.replace(/\s*Support:\s*support@(?:easyclaw|easyclawmart)\.pro\.?/i, "")
    .trim();

  const checkoutPaused = process.env.NEXT_PUBLIC_PAUSE_CHECKOUT === "true";

  const locale = useLocale();
  const { setShowSignModal } = useAppContext();
  const uiSource = isStandalone ? "pricing_page" : "pricing_section";

  const [group, setGroup] = useState(() => {
    const featuredGroup = pricing.groups?.find((entry) => entry.is_featured);
    return featuredGroup?.name || pricing.groups?.[0]?.name;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProductId, setLoadingProductId] = useState<string | null>(null);
  const [manualPayItem, setManualPayItem] = useState<PricingItem | null>(null);
  const [isManualPayModalOpen, setIsManualPayModalOpen] = useState(false);

  const visibleItems = useMemo(
    () => pricing.items?.filter((item) => !item.group || item.group === group) ?? [],
    [group, pricing.items]
  );
  const activeGroup = pricing.groups?.find((item) => item.name === group);
  const isChineseLocale = locale?.startsWith("zh") || false;

  const isAuthError = (message?: string | null) => {
    if (!message) return false;
    const normalized = message.toLowerCase();
    return (
      normalized.includes("no auth") ||
      normalized.includes("unauth") ||
      normalized.includes("sign-in") ||
      normalized.includes("sign in")
    );
  };

  const handleCheckout = async (item: PricingItem, cnPay = false) => {
    try {
      const eventCurrency = (cnPay ? "cny" : item.currency) || "usd";
      const gaCurrency = eventCurrency.toUpperCase();
      const amountMinor =
        cnPay && typeof item.cn_amount === "number" ? item.cn_amount : item.amount;
      const value = minorToMajorCurrencyUnits(amountMinor, eventCurrency);

      const gaItem = {
        item_id: item.product_id,
        item_name: item.product_name || item.title || item.product_id,
        price: value,
        currency: gaCurrency,
        quantity: 1,
      };

      const emitCheckoutError = ({
        httpStatus,
        errorCode,
        provider,
      }: {
        httpStatus?: number;
        errorCode?: string | null;
        provider?: string | null;
      }) => {
        trackEvent("checkout_error", {
          plan_id: item.product_id,
          http_status: typeof httpStatus === "number" ? httpStatus : undefined,
          error_code: errorCode || "unknown",
          provider: provider || "unknown",
          ui_source: uiSource,
        });
      };

      trackEvent("select_item", {
        item_list_id: "pricing",
        item_list_name: group || "",
        items: [gaItem],
      });

      trackEvent("view_item", {
        currency: gaCurrency,
        value,
        items: [gaItem],
      });

      trackEvent("begin_checkout", {
        plan_id: item.product_id,
        interval: item.interval,
        currency: gaCurrency,
        value,
        items: [gaItem],
        ui_source: uiSource,
      });

      setIsLoading(true);
      setLoadingProductId(item.product_id);

      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_id: item.product_id,
          currency: eventCurrency,
          locale: locale || "en",
        }),
      });

      const payload = await response.json().catch(() => null);

      if (response.status === 401) {
        emitCheckoutError({ httpStatus: response.status, errorCode: "http_401" });
        setShowSignModal(true);
        return;
      }

      if (!payload) {
        emitCheckoutError({
          httpStatus: response.status,
          errorCode: "invalid_json",
        });
        toast.error("checkout failed");
        return;
      }

      const { code, message, data } = payload;
      if (code !== 0) {
        emitCheckoutError({
          httpStatus: response.status,
          errorCode:
            (typeof data?.error_code === "string" && data.error_code) ||
            (typeof code === "number" ? String(code) : "unknown"),
          provider: data?.provider,
        });
        if (isAuthError(message)) {
          setShowSignModal(true);
          return;
        }
        toast.error(message || "checkout failed");
        return;
      }

      if (!data?.checkout_url) {
        emitCheckoutError({
          httpStatus: response.status,
          errorCode: "missing_checkout_url",
          provider: data?.provider,
        });
        toast.error("checkout failed");
        return;
      }

      window.location.href = data.checkout_url;
    } catch (error) {
      console.log("checkout failed: ", error);
      trackEvent("checkout_error", {
        plan_id: item.product_id,
        error_code: "exception",
        ui_source: uiSource,
      });
      toast.error("checkout failed");
    } finally {
      setIsLoading(false);
      setLoadingProductId(null);
    }
  };

  const handleManualPay = async (item: PricingItem) => {
    trackEvent("begin_checkout", {
      plan_id: item.product_id,
      interval: item.interval,
      currency: "CNY",
      value: (item.cn_amount || 0) / 100,
      ui_source: uiSource,
      payment_method: "manual_qr",
    });

    setManualPayItem(item);
    setIsManualPayModalOpen(true);
  };

  const viewItemListKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!visibleItems.length) return;

    const key = `${group || ""}:${visibleItems.map((item) => item.product_id).join(",")}`;
    if (viewItemListKeyRef.current === key) return;

    viewItemListKeyRef.current = key;

    trackEvent("view_item_list", {
      item_list_id: "pricing",
      item_list_name: group || "",
      items: visibleItems.map((item) => ({
        item_id: item.product_id,
        item_name: item.product_name || item.title || item.product_id,
        price: minorToMajorCurrencyUnits(item.amount, item.currency || "usd"),
        currency: (item.currency || "usd").toUpperCase(),
        quantity: 1,
      })),
    });
  }, [group, visibleItems]);

  const TitleTag = isStandalone ? "h1" : "h2";

  return (
    <>
      <section
        id={pricing.name}
        className="relative overflow-hidden px-4 pb-20 pt-16 sm:px-6 lg:px-8 lg:pb-24 lg:pt-20"
        data-testid="mirofish-pricing-page"
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-[8%] top-[5%] h-[320px] w-[320px] rounded-full bg-sky-500/16 blur-[120px]" />
          <div className="absolute right-[10%] top-[16%] h-[280px] w-[280px] rounded-full bg-orange-500/14 blur-[120px]" />
          <div className="absolute bottom-[-10%] left-1/2 h-[360px] w-[360px] -translate-x-1/2 rounded-full bg-violet-500/12 blur-[150px]" />
        </div>

        <div className="relative mx-auto max-w-7xl">
          <div className="mx-auto max-w-3xl text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/6 px-4 py-2 text-xs uppercase tracking-[0.24em] text-white/65">
              <Sparkles className="h-4 w-4 text-amber-300" />
              {pricing.label || "Simulation Access"}
            </div>
            <TitleTag className="mt-6 text-balance text-4xl font-semibold tracking-[-0.04em] text-white sm:text-5xl lg:text-6xl">
              {pricing.title}
            </TitleTag>
            {description ? (
              <p className="mt-5 text-lg leading-8 text-white/72">{description}</p>
            ) : null}
          </div>

          {pricing.groups?.length ? (
            <div className="mt-10 flex flex-col items-center gap-3">
              <div className="rounded-full border border-white/10 bg-white/5 p-2 shadow-[0_24px_80px_rgba(0,0,0,0.35)] backdrop-blur-xl">
                <RadioGroup
                  value={group}
                  className="grid gap-2"
                  style={{
                    gridTemplateColumns: `repeat(${pricing.groups.length}, minmax(0, 1fr))`,
                  }}
                  onValueChange={(value) => setGroup(value)}
                >
                  {pricing.groups.map((item) => (
                    <div key={item.name} className="rounded-full">
                      <RadioGroupItem
                        value={item.name || ""}
                        id={item.name}
                        className="peer sr-only"
                      />
                      <Label
                        htmlFor={item.name}
                        className={cn(
                          "flex cursor-pointer items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition",
                          group === item.name
                            ? "bg-white text-black"
                            : "text-white/62 hover:text-white"
                        )}
                      >
                        {item.title}
                        {item.label ? (
                          <Badge
                            variant={group === item.name ? "secondary" : "outline"}
                            className={cn(
                              "px-1.5 text-[10px] uppercase tracking-[0.18em]",
                              group === item.name
                                ? "border-black/10 bg-black/10 text-black"
                                : "border-white/12 bg-white/8 text-white/75"
                            )}
                          >
                            {item.label}
                          </Badge>
                        ) : null}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {activeGroup?.description ? (
                <p className="max-w-2xl text-center text-sm leading-6 text-white/50">
                  {activeGroup.description}
                </p>
              ) : null}
            </div>
          ) : null}

          <div
            className={cn(
              "mt-10 grid gap-6 lg:gap-8",
              visibleItems.length === 1
                ? "mx-auto max-w-2xl grid-cols-1"
                : visibleItems.length === 2
                  ? "md:grid-cols-2"
                  : "md:grid-cols-2 lg:grid-cols-3"
            )}
          >
            {visibleItems.map((item, index) => {
              const isItemLoading = isLoading && loadingProductId === item.product_id;
              const showManualPay = isChineseLocale && item.cn_amount && item.cn_amount > 0;

              return (
                <article
                  key={item.product_id}
                  className={cn(
                    "relative overflow-hidden rounded-[32px] border p-6 shadow-[0_30px_100px_rgba(0,0,0,0.36)] backdrop-blur-xl md:p-8",
                    item.is_featured
                      ? "border-white/18 bg-[linear-gradient(180deg,rgba(255,255,255,0.14),rgba(255,255,255,0.06))]"
                      : "border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.03))]"
                  )}
                  data-testid="mirofish-pricing-card"
                  data-product-id={item.product_id}
                >
                  <div className="pointer-events-none absolute inset-0">
                    <div
                      className={cn(
                        "absolute -right-16 top-[-3rem] h-40 w-40 rounded-full blur-3xl",
                        item.is_featured
                          ? "bg-orange-400/24"
                          : index % 2 === 0
                            ? "bg-sky-400/18"
                            : "bg-violet-400/18"
                      )}
                    />
                    <div className="absolute bottom-0 left-0 h-px w-full bg-gradient-to-r from-transparent via-white/14 to-transparent" />
                  </div>

                  <div className="relative flex h-full flex-col">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        {item.label ? (
                          <Badge className="rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-white/76 shadow-none hover:bg-white/10">
                            {item.label}
                          </Badge>
                        ) : null}
                        {item.title ? (
                          <h3 className="mt-5 text-3xl font-semibold tracking-[-0.03em] text-white">
                            {item.title}
                          </h3>
                        ) : null}
                      </div>

                    </div>

                    {item.description ? (
                      <p className="mt-4 max-w-md text-sm leading-7 text-white/66">
                        {item.description}
                      </p>
                    ) : null}

                    <div className="mt-8">
                      <div className="flex items-end gap-3">
                        {item.price ? (
                          <span className="text-5xl font-semibold tracking-[-0.05em] text-white">
                            {item.price}
                          </span>
                        ) : null}
                        {item.unit ? (
                          <span className="mb-2 text-sm uppercase tracking-[0.18em] text-white/45">
                            {item.unit}
                          </span>
                        ) : null}
                      </div>

                      {item.original_price ? (
                        <div className="mt-3 text-sm uppercase tracking-[0.2em] text-white/35 line-through">
                          {item.original_price}
                        </div>
                      ) : null}
                    </div>

                    {item.features_title ? (
                      <p className="mt-8 text-xs uppercase tracking-[0.24em] text-white/42">
                        {item.features_title}
                      </p>
                    ) : null}

                    {item.features?.length ? (
                      <ul className="mt-4 flex flex-col gap-3">
                        {item.features.map((feature, featureIndex) => (
                          <li
                            key={`${item.product_id}-${featureIndex}`}
                            className="flex items-start gap-3 text-sm leading-6 text-white/78"
                          >
                            <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/8">
                              <Check className="h-3.5 w-3.5 text-sky-300" />
                            </span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}

                    <div className="mt-8 flex flex-1 flex-col justify-end gap-3">
                      {showManualPay && !checkoutPaused ? (
                        <Button
                          variant="outline"
                          className="h-12 w-full justify-center gap-2 rounded-full border-green-500/40 bg-green-500/8 font-semibold text-green-100 hover:bg-green-500/16 hover:text-green-50"
                          disabled={isItemLoading}
                          onClick={() => {
                            if (isLoading) return;
                            void handleManualPay(item);
                          }}
                        >
                          {isItemLoading ? (
                            <>
                              <Loader className="mr-2 h-4 w-4 animate-spin" />
                              处理中...
                            </>
                          ) : (
                            <>
                              <Image
                                src="https://pub-e1eb76428e24457ebfc067c635cb4fc4.r2.dev/imgs/cnpay.png"
                                alt="CNY"
                                width={20}
                                height={20}
                                className="h-5 w-5"
                              />
                              支付宝/微信 ¥{(item.cn_amount || 0) / 100}
                            </>
                          )}
                        </Button>
                      ) : null}

                      {checkoutPaused ? (
                        <p className="text-sm text-white/45">
                          New orders are not being accepted at this time.
                        </p>
                      ) : item.button ? (
                        <Button
                          data-testid={`pricing-cta-${item.product_id}`}
                          className={cn(
                            "h-12 w-full rounded-full font-semibold transition",
                            item.is_featured
                              ? "bg-white text-black hover:bg-white/92"
                              : "border border-white/12 bg-black/30 text-white hover:bg-black/42"
                          )}
                          disabled={isItemLoading}
                          onClick={() => {
                            if (isLoading) return;
                            void handleCheckout(item);
                          }}
                        >
                          {isItemLoading ? (
                            <>
                              <Loader className="mr-2 h-4 w-4 animate-spin" />
                              {item.button.title}
                            </>
                          ) : (
                            item.button.title
                          )}
                        </Button>
                      ) : null}

                      {item.tip ? (
                        <p className="text-sm leading-6 text-white/48">{item.tip}</p>
                      ) : null}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <div className="mx-auto mt-8 max-w-3xl text-center text-sm leading-6 text-white/42">
            Every plan keeps the same MiroFish workflow. You are choosing how much simulation
            headroom and iteration cadence you need.
          </div>
        </div>
      </section>

      <ManualPaymentModal
        isOpen={isManualPayModalOpen}
        onClose={() => setIsManualPayModalOpen(false)}
        item={manualPayItem}
      />
    </>
  );
}
